import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { AfterViewInit, Directive, DoCheck, ElementRef, Injector, Input, OnDestroy, inject, DestroyRef } from '@angular/core';
import { ScrollingButtonComponent } from './components/scrolling-button/scrolling-button.component';
import { PerfectScrollbarComponent } from 'ngx-perfect-scrollbar';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, interval } from 'rxjs';

import { SCROLLABLE_DIRECTION_TOKEN, SCROLLABLE_HTMLELEMENT_TOKEN } from './utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Directive({
  selector: '[scrollButtons]',
  exportAs: 'scrollButtons',
})
export class ScrollingButtonsDirective implements AfterViewInit, DoCheck, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private overlayRefs: OverlayRef[] = [];
  private container: HTMLElement;
  private leftButtonPortal: ComponentPortal<ScrollingButtonComponent>;
  private rightButtonPortal: ComponentPortal<ScrollingButtonComponent>;
  private leftButtonOverlayRef: OverlayRef;
  private rightButtonOverlayRef: OverlayRef;

  @Input()
  hideScrollButtons: boolean;

  private readonly hideScrollButtons$ = new BehaviorSubject<boolean>(undefined);
  private readonly scrollingDisableState$ = new BehaviorSubject<boolean>(undefined);
  private readonly containerIsNotVisible$ = new BehaviorSubject<boolean>(undefined);

  get isScrollDisabled(): boolean {
    return (
      this.container?.scrollLeft === 0 &&
      this.container.scrollLeft + this.container.clientWidth >= this.container.scrollWidth
    );
  }

  constructor(private overlay: Overlay, private injector: Injector) {}

  ngDoCheck(): void {
    this.scrollingDisableState$.next(this.isScrollDisabled);
    this.hideScrollButtons$.next(this.hideScrollButtons);
  }

  ngAfterViewInit(): void {
    interval(300)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.containerIsNotVisible$.next(!this.checkIfContainerExists());
      });

    this.container = this.getContainer();
    const overlayConfig1 = new OverlayConfig({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.container)
        .withPositions([{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: 20 }]),
    });

    const overlayConfig2 = new OverlayConfig({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.container)
        .withPositions([{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: -20 }]),
    });

    const leftButtonInjector = Injector.create({
      providers: [
        { provide: SCROLLABLE_DIRECTION_TOKEN, useValue: 'left' },
        { provide: SCROLLABLE_HTMLELEMENT_TOKEN, useValue: this.container },
      ],
      parent: this.injector,
    });

    const rightButtonInjector = Injector.create({
      providers: [
        { provide: SCROLLABLE_DIRECTION_TOKEN, useValue: 'right' },
        { provide: SCROLLABLE_HTMLELEMENT_TOKEN, useValue: this.container },
      ],
      parent: this.injector,
    });

    this.leftButtonOverlayRef = this.overlay.create(overlayConfig1);
    this.rightButtonOverlayRef = this.overlay.create(overlayConfig2);
    this.overlayRefs.push(this.leftButtonOverlayRef, this.rightButtonOverlayRef);

    this.leftButtonPortal = new ComponentPortal(ScrollingButtonComponent, undefined, leftButtonInjector);
    this.rightButtonPortal = new ComponentPortal(ScrollingButtonComponent, undefined, rightButtonInjector);

    this.showButtons();

    combineLatest([
      this.hideScrollButtons$.pipe(distinctUntilChanged()),
      this.scrollingDisableState$.pipe(distinctUntilChanged()),
      this.containerIsNotVisible$.pipe(distinctUntilChanged()),
    ])
      .pipe(
        filter(([hideByPurpose, disableByCondition, containerIsNotVisible]) => !(hideByPurpose == undefined && disableByCondition == undefined && containerIsNotVisible == undefined)),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([hideByPurpose, disableByCondition, containerIsNotVisible]) => {
        if (hideByPurpose || disableByCondition || containerIsNotVisible) {
          this.hideButtons();
        } else {
          this.showButtons();
        }
      });
  }

  ngOnDestroy(): void {
    this.overlayRefs.forEach((ref) => ref.dispose());
  }

  private checkIfContainerExists(): boolean {
    const rect = this.getContainer().getBoundingClientRect();
    // In case with having Tabs component using switcher the invisible tab content sets width to 2px
    return !(rect.width <= 2 || rect.height === 0);
  }

  private showButtons(): void {
    if (!this.leftButtonOverlayRef.hasAttached() && !this.rightButtonOverlayRef.hasAttached()) {
      this.leftButtonOverlayRef.attach(this.leftButtonPortal);
      this.rightButtonOverlayRef.attach(this.rightButtonPortal);
    }
  }

  private hideButtons(): void {
    this.leftButtonOverlayRef.detach();
    this.rightButtonOverlayRef.detach();
  }

  private getContainer(): HTMLElement {
    const perfectScrollComp: PerfectScrollbarComponent = this.injector.get(PerfectScrollbarComponent, null);

    if (perfectScrollComp) {
      return perfectScrollComp.directiveRef.elementRef.nativeElement;
    }

    return this.injector.get(ElementRef).nativeElement;
  }
}
